import { css } from '@styled-system/css';
import { type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';

// Création / renommage d'un dossier de recherches — porté d'Autocop
// (FolderFormModal), sans antd (erreurs inline uniquement).

export interface FolderFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (name: string) => void;
  mode: 'create' | 'rename';
  initialName?: string;
}

const formGroup = css({ display: 'flex', flexDirection: 'column', gap: '4', paddingTop: '4' });

const label = css({
  fontSize: 'bodySm',
  fontWeight: 'medium',
  color: 'text.secondary',
});

const errorText = css({
  fontSize: 'bodySm',
  color: 'error.default',
});

export const FolderFormModal: FC<FolderFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  mode,
  initialName = '',
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState(initialName);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setError(null);
    }
  }, [isOpen, initialName]);

  const handleSubmit = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t('folders.errorRequired'));
      return;
    }
    if (trimmed.length > 50) {
      setError(t('folders.errorTooLong'));
      return;
    }
    onSubmit(trimmed);
    setName('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'create' ? t('folders.createTitle') : t('folders.renameTitle')}
      width="420px"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            {mode === 'create' ? t('folders.create') : t('folders.rename')}
          </Button>
        </>
      }
    >
      <div className={formGroup}>
        <label className={label} htmlFor="folder-name">
          {t('folders.nameLabel')}
        </label>
        <Input
          id="folder-name"
          autoFocus
          placeholder={t('folders.namePlaceholder')}
          value={name}
          state={error ? 'error' : 'default'}
          onChange={(event) => {
            setName(event.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              handleSubmit();
            }
          }}
        />
        {error && <span className={errorText}>{error}</span>}
      </div>
    </Modal>
  );
};
